import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { verificarStatusPagamento } from '../api/vehicleAPI';
import Card from '../components/common/Card';
import { FaCheckCircle, FaExclamationCircle, FaSpinner, FaQrcode } from 'react-icons/fa';
import QRCodeDisplay from '../components/pagamento/QRCodeDisplay';
import PaymentInstructions from '../components/pagamento/PaymentInstructions';
import PaymentStatus from '../components/pagamento/PaymentStatus';
import PaymentSupport from '../components/pagamento/PaymentSupport';

const PageWrapper = styled.div` padding: 80px 20px 60px; display: flex; flex-direction: column; align-items: center; gap: 30px; `;
const fadeInUp = keyframes` to { opacity: 1; transform: translateY(0); } `;
const PaymentPixContainer = styled(Card)` padding: 0; width: 100%; max-width: 600px; text-align: center; overflow: hidden; opacity: 0; transform: translateY(20px); animation: ${fadeInUp} 0.5s ease forwards; `;
const PaymentHeader = styled.div` background: linear-gradient(135deg, #6C5CE7 0%, #00CEC9 100%); color: white; padding: 25px 30px; h2 { font-size: 24px; margin-bottom: 5px; display: flex; align-items: center; justify-content: center; gap: 12px; } `;
const PaymentContent = styled.div` padding: 30px; @media (max-width: 480px) { padding: 20px; } `;
const PixActions = styled.div` display: flex; flex-direction: column; gap: 15px; margin-bottom: 25px; `;
const RefreshButton = styled.button` padding: 14px 20px; border-radius: 16px; font-weight: 600; font-size: 16px; cursor: pointer; transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1); display: flex; align-items: center; justify-content: center; gap: 10px; border: none; background: #00B894; color: white; box-shadow: 0 2px 8px rgba(0,0,0,0.08); &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.12); opacity: 0.95; } &:disabled { background: #636E72; cursor: not-allowed; transform: none; box-shadow: none; } `;
const StatusWrapper = styled.div` padding: 40px 20px; h3 { margin-bottom: 10px; font-size: 22px; } p { font-size: 16px; } &.approved { color: #00B894; } &.error { color: #D63031; } `;
const Checkmark = styled(FaCheckCircle)` font-size: 48px; margin-bottom: 15px; color: #00B894;`;
const ErrorIcon = styled(FaExclamationCircle)` font-size: 48px; margin-bottom: 15px; color: #D63031;`;
const spin = keyframes` from { transform: rotate(0deg); } to { transform: rotate(360deg); } `;
const LoadingSpinner = styled(FaSpinner)` animation: ${spin} 1s linear infinite; font-size: 24px; margin-bottom: 15px; `;
const SmallSpinner = styled(LoadingSpinner)` font-size: 16px; margin: 0; `;

const redirecionarAposPagamento = (produtos, orderIdFinal, placa, navigate) => {
  if (!navigate || !placa || !orderIdFinal || !Array.isArray(produtos)) {
    console.warn("Parâmetros inválidos para redirecionamento", {
      produtos,
      orderIdFinal,
      placa,
      navigate
    });
    return;
  }

  const hasOnlyConsultaCompleta = produtos.length === 1 && 
    ['CONSULTA_COMPLETA', 'RESULTADO_COMPLETO'].includes(produtos[0].id || produtos[0].tipo);

  if (hasOnlyConsultaCompleta) {
    navigate(`/relatorio_completo/${orderIdFinal}/${placa}`, { state: { produtos } });
  } else {
    navigate(`/relatorio_upsell/${orderIdFinal}/${placa}`, { state: { produtos } });
  }
};


const getInitialPaymentData = (location) => {
    if (location.state?.paymentData) {
        sessionStorage.setItem('paymentData', JSON.stringify(location.state.paymentData));
        return location.state.paymentData;
    }
    const storedData = sessionStorage.getItem('paymentData');
    return storedData ? JSON.parse(storedData) : null;
};

const QRCodePage = () => {
    const { placa, orderId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [paymentData, setPaymentData] = useState(() => getInitialPaymentData(location));
    const [isPaid, setIsPaid] = useState(false);
    const [error, setError] = useState(null);
    const [isCheckingManually, setIsCheckingManually] = useState(false);

    const handlePaymentSuccess = useCallback(() => {
        setIsPaid(true);
        sessionStorage.removeItem('paymentData');

        setTimeout(() => {
            const produtos = paymentData?.produtos || paymentData?.itens || [];
            const orderIdFinal = orderId || paymentData?.orderId || paymentData?.id;

            redirecionarAposPagamento(produtos, orderIdFinal, placa, navigate);
        }, 2000);
    }, [navigate, placa, paymentData, orderId]);

    const handleManualCheck = async () => {
        if (!paymentData?.id) return;
        setIsCheckingManually(true);
        try {
            const response = await verificarStatusPagamento(paymentData.id);
            const statusPago = Array.isArray(response)
                ? response[0]?.status === 'paid'
                : response?.status === 'paid';

            if (statusPago) {
                handlePaymentSuccess();
            }
        } catch (err) {
            console.error("Erro na verificação manual:", err);
            alert("Ocorreu um erro ao verificar o pagamento.");
        } finally {
            setIsCheckingManually(false);
        }
    };

    useEffect(() => {
        if (!paymentData) {
            setError("Dados de pagamento não encontrados.");
        }
    }, [paymentData]);

    const renderPaymentContent = () => {
        if (error) {
            return <StatusWrapper className="error"><ErrorIcon /><p>{error}</p></StatusWrapper>;
        }
        if (isPaid) {
            return <StatusWrapper className="approved"><Checkmark /><h3>Pagamento Aprovado!</h3><p>Redirecionando...</p></StatusWrapper>;
        }
        if (paymentData) {
            return (
                <>
                    <QRCodeDisplay paymentData={paymentData} />
                    <PixActions>
                        <RefreshButton onClick={handleManualCheck} disabled={isCheckingManually}>
                            {isCheckingManually ? <SmallSpinner /> : <FaCheckCircle />}
                            {isCheckingManually ? 'Verificando...' : 'Já Paguei, verificar agora'}
                        </RefreshButton>
                    </PixActions>
                    <PaymentStatus 
                        paymentId={paymentData.id}
                        onSuccess={handlePaymentSuccess} 
                    />
                </>
            );
        }
        return <><LoadingSpinner /> <p>Aguarde...</p></>;
    };

    return (
        <PageWrapper>
            <PaymentPixContainer>
                <PaymentHeader>
                    <h2><FaQrcode /> Pagamento via PIX</h2>
                    <p>Escaneie o QR Code ou copie o código para pagar</p>
                </PaymentHeader>
                <PaymentContent>
                    {renderPaymentContent()}
                </PaymentContent>
            </PaymentPixContainer>

            {paymentData && !isPaid && !error && <PaymentInstructions />}

            <PaymentSupport />
        </PageWrapper>
    );
};

export default QRCodePage;
